import { all, select, takeEvery, fork, call, put } from 'redux-saga/effects'

// action
import {
	configActions,
	adminActions,
	mainActions,
	routeActions,
	// mateActions,
} from '@redux/store/index'
import { defaultCreateTempMenu, emptyMenu } from '@redux/store/admin/admin.store'

// api
import { menuApi, i18nApi, ResType, ParameterType } from '@api'

// lib
import shortid from 'shortid'
import { Logger, Path } from '@utils'
import { produce } from 'immer'

// schema
import { Menu, MenuTreeNode, I18n, I18nType } from '@schemas'

// type
import type {
	AdminStateType,
	EditMenuType,
	MenuTreeNodeType,
	MenuType,
	CrudType,
} from '@redux/types'

const apiAddMenu = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await menuApi.addMenu(data)
	return res
}

const apiGetMemu = async (): Promise<ResType | undefined> => {
	const res: ResType | undefined = await menuApi.getMenu()
	return res
}

const apiUpdateMultipleMenu = async (
	data: ParameterType,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await menuApi.updateMultipleMenu(data)
	return res
}

const apiBatchDeleteMenu = async (query: {
	menu_ids: string
}): Promise<ResType | undefined> => {
	const res: ResType | undefined = await menuApi.batchDeleteMenu({ query: query })
	return res
}

const apiUpdateI18n = async (paramsId: string, body: any) => {
	const res: ResType | undefined = await i18nApi.updateI18n({
		params: { id: paramsId },
		body: body,
	})
	return res
}

function* goToAdminSaga() {
	try {
		yield put(configActions.IS_LOADING(true))
		yield put(routeActions.NAVIGATE_PATH('/admin'))
	} catch (error) {
		Logger.warn(error)
	}
}

function* leaveToAdminSaga() {
	try {
		yield put(configActions.IS_LOADING(true))
		yield put(configActions.MODE('main'))
	} catch (error) {
		Logger.warn(error)
	}
}

function* initAdminMenuPageSaga() {
	try {
		yield put(mainActions.TRIGGER_GET_MENU(true))
		yield put(adminActions.EDIT_TEMP_MENU({ ...emptyMenu }))
		yield put(adminActions.SELECTED_MENU({ ...emptyMenu }))
	} catch (error) {
		Logger.warn(error)
	}
}

function* initCreateMenuPageSaga() {
	try {
		const editMenuMode: AdminStateType['editMenuMode'] = yield select(
			state => state.admin.editMenuMode,
		)
		const menuTreeList: MenuTreeNodeType[] = yield select(
			state => state.admin.menuTreeList,
		)

		shortid.characters('0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ!@')
		const uniqueId: string = shortid()
		const menuDirId = editMenuMode === 'create-dir' ? `MD-${uniqueId}` : `OP-${uniqueId}`

		const menuNameArr: string[] = menuTreeList
			.filter((aMenu: MenuTreeNodeType) => aMenu.parent === defaultCreateTempMenu.parent)
			.map((aMenu2: MenuTreeNodeType) => aMenu2.text)

		const tempDefaultname: string =
			editMenuMode === 'create-dir' ? 'New Folder' : 'New Object'
		const unduplicatedMenuName: string =
			editMenuMode === 'create-dir'
				? Path.getUnduplicatedMenuDirName(menuNameArr, tempDefaultname)
				: Path.getUnduplicatedMenuObjectName(menuNameArr, tempDefaultname)

		if (unduplicatedMenuName !== '') {
			const newMenu: EditMenuType = produce(defaultCreateTempMenu, draft => {
				const newName: string = unduplicatedMenuName
				const newUri: string = Path.getUriByPath(`/${unduplicatedMenuName}`)

				draft.id = menuDirId
				draft.icon = editMenuMode === 'create-dir' ? 'cog' : 'circle-small'
				draft.name = newName
				draft.path = `root/${unduplicatedMenuName}`
				draft.uri = newUri
			})

			yield put(adminActions.EDIT_TEMP_MENU(newMenu))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* getMenuListSaga() {
	try {
		const apiRes: ResType | undefined = yield call(apiGetMemu)

		if (apiRes !== undefined && apiRes.status === 200) {
			const tempmMenuTreeList: MenuTreeNodeType[] = apiRes.data.map(
				(aData: { [key: string]: any }) =>
					new MenuTreeNode({
						...aData,
						text: aData.name,
						droppable: aData.folderYn === 1 ? true : false,
						isActivate: aData.activateYn === 1 ? true : false,
						i18n: aData.i18n,
					}),
			)
			const newMenuTreeList: MenuTreeNodeType[] = Path.orderMenuTree(tempmMenuTreeList)

			yield put(adminActions.MENU_TREE_LIST([...newMenuTreeList]))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* createMenuSaga() {
	try {
		const editMenuMode: AdminStateType['editMenuMode'] = yield select(
			state => state.admin.editMenuMode,
		)
		const editTempMenu: EditMenuType = yield select(state => state.admin.editTempMenu)
		const menuTreeList: MenuTreeNodeType[] = yield select(
			state => state.admin.menuTreeList,
		)
		const menuNameArr: string[] = menuTreeList
			.filter((aMenu: MenuTreeNodeType) => aMenu.parent === editTempMenu.parent)
			.map((aMenu2: MenuTreeNodeType) => aMenu2.text)
		const newUnduplicatedName = Path.getUnduplicatedMenuDirName(
			menuNameArr,
			editTempMenu.name,
		)

		const isDuplicatedId: boolean = newUnduplicatedName !== editTempMenu.name

		if (isDuplicatedId) {
			yield put(adminActions.UNDUPLICATED_NAME(newUnduplicatedName))
			yield put(adminActions.MENU_OPEN_MODAL('duplicatedNameAlert'))
		} else {
			const newOrder = menuTreeList.filter(
				aNode => aNode.droppable && aNode.parent === editTempMenu.parent,
			)
			const isActivate: number =
				editTempMenu.isActivate === undefined ? 0 : editTempMenu.isActivate ? 1 : 0
			const body = {
				id: editTempMenu.id,
				name: editTempMenu.name,
				type: editTempMenu.type,
				order: newOrder.length,
				path: editTempMenu.path,
				uri: editTempMenu.uri,
				icon: editTempMenu.icon,
				parentId: editTempMenu.parent,
				folderYn: editMenuMode === 'create-dir' ? 1 : 0,
				activateYn: isActivate,
			}

			const apiRes: ResType | undefined = yield call(apiAddMenu, { body: { ...body } })

			if (apiRes !== undefined && apiRes.status === 201) {
				yield put(adminActions.TRIGGER_CREATE_MENU_SUCCESS_RESULT(true))
				yield put(adminActions.TRIGGER_GET_MENU_LIST(true))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* modifyMenuDirSaga() {
	try {
		const editTempMenu: EditMenuType = yield select(state => state.admin.editTempMenu)
		const menuTreeList: MenuTreeNodeType[] = yield select(
			state => state.admin.menuTreeList,
		)
		const menuNameArr: string[] = menuTreeList
			.filter((aMenu: MenuTreeNodeType) => aMenu.parent === editTempMenu.parent)
			.map((aMenu2: MenuTreeNodeType) => aMenu2.text)
		const OrginMenuNodeList: MenuTreeNodeType[] = menuTreeList.filter(
			(aMenu2: MenuTreeNodeType) => aMenu2.id === editTempMenu.id,
		)
		const OrginMenuNode: MenuTreeNodeType | undefined =
			OrginMenuNodeList.length > 0 ? OrginMenuNodeList[0] : undefined
		const OrginName: string = OrginMenuNode !== undefined ? OrginMenuNode['text'] : ''
		const newUnduplicatedName = Path.getUnduplicatedMenuDirName(
			menuNameArr,
			editTempMenu.name,
		)
		const isDuplicatedId: boolean =
			OrginName !== editTempMenu.name && newUnduplicatedName !== editTempMenu.name

		if (isDuplicatedId) {
			yield put(adminActions.UNDUPLICATED_NAME(newUnduplicatedName))
			yield put(adminActions.MENU_OPEN_MODAL('duplicatedNameAlert'))
		} else {
			const isFolder: boolean = editTempMenu.id.startsWith('MD-')
			const isActivate: number =
				editTempMenu.isActivate === undefined ? 0 : editTempMenu.isActivate ? 1 : 0

			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { i18n, createdAt, updatedAt, updatedBy, parentId, ...updateMenu } = new Menu(
				{
					...editTempMenu,
					i18n: editTempMenu['i18n'] as any,
					parentId: editTempMenu.parent,
					folderYn: isFolder ? 1 : 0,
					activateYn: isActivate,
				},
			)

			const body = {
				menuList: [{ ...updateMenu, parentId: parentId }],
			}

			const apiRes: ResType | undefined = yield call(apiUpdateMultipleMenu, {
				body: { ...body },
			})

			if (apiRes !== undefined && apiRes.status === 201) {
				yield put(adminActions.TRIGGER_MODIFY_MENU_SUCCESS_RESULT(true))
				yield put(adminActions.TRIGGER_GET_MENU_LIST(true))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

interface DeleteBodyResultType {
	deleteDirIdArr: string[]
	updateObjectList: MenuType[]
}

function* deleteMenuSaga() {
	try {
		const crud: CrudType = yield select(state => state.auth.currentAccess)
		const editTempMenu: EditMenuType = yield select(state => state.admin.editTempMenu)
		const menuTreeList: MenuTreeNodeType[] = yield select(
			state => state.admin.menuTreeList,
		)

		const isDir: boolean = editTempMenu.id.startsWith('MD-')
		const isAllowDeleteDir: boolean = crud.includes('D') !== null && isDir

		const isAllowDeleteObject: boolean = crud.includes('D') !== null && !isDir

		const { deleteDirIdArr, updateObjectList } = yield call(getDeletingBody, {
			menuTreeList: menuTreeList,
			targetId: editTempMenu.id,
			isDir: isDir,
			isAllowDeleteDir: isAllowDeleteDir,
			isAllowDeleteObject: isAllowDeleteObject,
		})

		if (deleteDirIdArr.length > 0 || updateObjectList.length > 0) {
			const apiDeleteRes: ResType | undefined =
				deleteDirIdArr.length > 0
					? yield call(apiBatchDeleteMenu, {
							menu_ids: deleteDirIdArr.join('|'),
					  })
					: undefined

			const apiUpdateRes: ResType | undefined =
				updateObjectList.length > 0
					? yield call(apiUpdateMultipleMenu, {
							body: { menuList: updateObjectList },
					  })
					: undefined

			if (
				(apiUpdateRes !== undefined && apiUpdateRes.status === 201) ||
				(apiDeleteRes !== undefined && apiDeleteRes.status === 200)
			) {
				yield put(adminActions.TRIGGER_DELETE_MENU_SUCCESS_RESULT(true))
				yield put(adminActions.TRIGGER_GET_MENU_LIST(true))
				yield put(adminActions.EDIT_TEMP_MENU({ ...emptyMenu }))
				yield put(adminActions.SELECTED_MENU({ ...emptyMenu }))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

const getDeletingBody = ({
	menuTreeList,
	targetId,
	isDir,
	isAllowDeleteDir,
	isAllowDeleteObject,
}: {
	menuTreeList: MenuTreeNodeType[]
	targetId: string
	isDir: boolean
	isAllowDeleteDir: boolean
	isAllowDeleteObject: boolean
}): DeleteBodyResultType => {
	if (isDir) {
		if (isAllowDeleteDir) {
			const dirRes: DeleteBodyResultType = isAllowDeleteObject
				? getDeleteDirWithDevDeleteAccess({ menuTreeList, targetId })
				: getDeleteDirWithoutDevDeleteAccess({ menuTreeList, targetId })

			return dirRes
		} else {
			return {
				deleteDirIdArr: [],
				updateObjectList: [],
			}
		}
	} else {
		const objRes: DeleteBodyResultType = isAllowDeleteObject
			? getDeleteObjectIds(targetId)
			: {
					deleteDirIdArr: [],
					updateObjectList: [],
			  }
		return objRes
	}
}

const getDeleteDirWithDevDeleteAccess = ({
	menuTreeList,
	targetId,
}: {
	menuTreeList: MenuTreeNodeType[]
	targetId: string
}): DeleteBodyResultType => {
	const targetNode: MenuTreeNodeType[] = menuTreeList.filter(
		aNode => aNode.id === targetId,
	)
	const descendantList: MenuTreeNodeType[] = Path.findDescendants(menuTreeList, [
		targetId,
	])
	const tempDeleteItems: MenuTreeNodeType[] = [...descendantList, ...targetNode]

	const deleteIdArr: string[] = tempDeleteItems.map(
		aNode => aNode.id.toString() ?? aNode.id,
	)

	const deleteDirIdArr: string[] = [...deleteIdArr]
	const updateObjectList: MenuType[] = []

	return {
		deleteDirIdArr,
		updateObjectList,
	}
}

const getDeleteDirWithoutDevDeleteAccess = ({
	menuTreeList,
	targetId,
}: {
	menuTreeList: MenuTreeNodeType[]
	targetId: string
}): DeleteBodyResultType => {
	const targetNode: MenuTreeNodeType[] = menuTreeList.filter(
		aNode => aNode.id === targetId,
	)
	const descendantList: MenuTreeNodeType[] = Path.findDescendants(menuTreeList, [
		targetId,
	])
	const tempDeleteItems: MenuTreeNodeType[] = [...descendantList, ...targetNode]
	const deleteDirIdArr: string[] = tempDeleteItems
		.filter(aNode => {
			const id: string = aNode.id.toString() ?? aNode.id
			return id.startsWith('MD-')
		})
		.map(aDirNode => aDirNode.id.toString() ?? aDirNode.id)

	const updateObjectList: MenuType[] = tempDeleteItems
		.filter(anObjNode => {
			const id: string = anObjNode.id.toString() ?? anObjNode.id
			return id.startsWith('OP-')
		})
		.map(anObjNode => {
			const objId: string = anObjNode.id.toString() ?? anObjNode.id
			const newPath: string = Path.getPathByName('root/', anObjNode.text)
			const newUri: string = Path.getUriByPath(newPath)

			const res = {
				id: objId,
				name: anObjNode.text,
				order: anObjNode.order,
				icon: anObjNode.icon,
				path: newPath,
				uri: newUri,
				parentId: 'root',
				folderYn: anObjNode.droppable ? 1 : 0,
				activateYn: anObjNode.isActivate ? 1 : 0,
			}

			return res
		})

	return {
		deleteDirIdArr,
		updateObjectList,
	}
}

const getDeleteObjectIds = (targetId: string): DeleteBodyResultType => {
	const deleteDirIdArr: string[] = [targetId]
	const updateObjectList: MenuType[] = []

	return {
		deleteDirIdArr,
		updateObjectList,
	}
}

function* menuTreeListSaga() {
	try {
		const menuTreeList: MenuTreeNodeType[] = yield select(
			state => state.admin.menuTreeList,
		)
		const menu: MenuType[] = yield select(state => state.main.menu)

		const result = Path.convertTreeNodeToMenu(menuTreeList)

		if (JSON.stringify(menu) !== JSON.stringify(result)) {
			yield put(mainActions.MENU(result))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateMultipleMenuListSaga() {
	try {
		const updateMultipleMenuList: MenuTreeNodeType[] = yield select(
			state => state.admin.updateMultipleMenuList,
		)

		const tempNewTree: MenuTreeNodeType[] = Path.orderMenuTree(updateMultipleMenuList)
		const isDuplicated: boolean = Path.checkDuplicatedNameInSamePath(tempNewTree)

		if (isDuplicated) {
			yield put(adminActions.MENU_OPEN_MODAL('duplicatedNameWithMovingAlert'))
		} else {
			const newMenuList = [...tempNewTree].map((aNode: MenuTreeNodeType) => {
				const id: string = aNode.id.toString() ?? aNode.id
				const parentId: string = aNode.parent.toString() ?? aNode.parent
				const isFolder: number =
					aNode.droppable === undefined ? 0 : aNode.droppable ? 1 : 0
				const isActivate: number =
					aNode.isActivate === undefined ? 0 : aNode.isActivate ? 1 : 0
				const newI18n: I18nType[] = (aNode.i18n as any as I18nType[]) ?? []

				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { i18n, createdAt, updatedAt, updatedBy, ...res } = new Menu({
					...aNode,
					id: id,
					name: aNode.text,
					order: aNode.order,
					icon: aNode.icon,
					parentId: parentId,
					folderYn: isFolder,
					activateYn: isActivate,
					i18n: newI18n as any,
				})

				return res
			})

			const body = {
				menuList: newMenuList,
			}

			const apiRes: ResType | undefined = yield call(apiUpdateMultipleMenu, {
				body: { ...body },
			})

			if (apiRes !== undefined && apiRes.status === 201) {
				yield put(adminActions.TRIGGER_GET_MENU_LIST(true))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateMenuI18nSaga() {
	try {
		const i18n: I18nType = yield select(state => state.admin.updateMenuI18n)
		const editTempMenu: EditMenuType = yield select(state => state.admin.editTempMenu)
		const prevI18n = editTempMenu.i18n.filter(
			anI18n => anI18n.isoLanguageCode === i18n.isoLanguageCode,
		)

		const isTitleUpdated: boolean =
			prevI18n.length === 0 || (prevI18n.length > 0 && prevI18n[0].title !== i18n.title)

		if (isTitleUpdated) {
			const body = {
				tableName: i18n.tableName,
				tableId: i18n.tableId,
				menuId: i18n.menuId,
				isoLanguageCode: i18n.isoLanguageCode,
				value: i18n.value,
				title: i18n.title,
			}

			const res: ResType | undefined = yield call(apiUpdateI18n, i18n.id, body)

			if (res !== undefined && res.status === 201) {
				const newI18n = new I18n(res.data[0] as I18nType)
				const i18nIndex = editTempMenu.i18n.findIndex(
					anI18n => anI18n.isoLanguageCode === newI18n.isoLanguageCode,
				)
				const newI18nList: I18nType[] =
					i18nIndex === -1
						? [...editTempMenu.i18n, newI18n]
						: produce<I18nType[]>([...editTempMenu.i18n], draft => {
								draft.splice(i18nIndex, 1)
								draft.push(newI18n)
						  })

				const newEditTempMenu = { ...editTempMenu, i18n: newI18nList }

				yield put(adminActions.EDIT_TEMP_MENU(newEditTempMenu))
				yield put(adminActions.TRIGGER_GET_MENU_LIST(true))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	// trigger
	yield takeEvery(adminActions.TRIGGER_GO_TO_ADMIN, goToAdminSaga)
	yield takeEvery(adminActions.TRIGGER_LEAVE_TO_ADMIN, leaveToAdminSaga)
	yield takeEvery(adminActions.TRIGGER_ADMIN_MENU_PAGE, initAdminMenuPageSaga)
	yield takeEvery(adminActions.TRIGGER_CREATE_MENU_PAGE, initCreateMenuPageSaga)
	yield takeEvery(adminActions.TRIGGER_GET_MENU_LIST, getMenuListSaga)
	yield takeEvery(adminActions.TRIGGER_CREATE_MENU, createMenuSaga)
	yield takeEvery(adminActions.TRIGGER_MODIFY_MENU, modifyMenuDirSaga)
	yield takeEvery(adminActions.TRIGGER_DELETE_MENU, deleteMenuSaga)

	// menu
	yield takeEvery(adminActions.MENU_TREE_LIST, menuTreeListSaga)

	// menu aciton
	yield takeEvery(adminActions.UPDATE_MULTIPLE_MENU_LIST, updateMultipleMenuListSaga)
	yield takeEvery(adminActions.UPDATE_MENU_I18N, updateMenuI18nSaga)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
